import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Truck, ChevronDown } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { getActiveUsers } from "../../api/user";
import { cn } from "../../utils/cn";

/**
 * Autocomplete selector for the courier's ACTIVE drivers (role "delivery").
 * Used by the contact panel to link a helpdesk contact to a driver.
 *
 * Same data source as /admin/users (getActiveUsers, server-side paginated)
 * with a high limit so the whole roster comes in one request; the name search
 * is client-side because the endpoint only searches by email. The roster is
 * loaded lazily (first time the list opens) and cached per logistic company,
 * like CommerceSelector's client cache.
 *
 * Props:
 *   - value: { id, name } | null   current driver (marked as "Actual")
 *   - onChange(driver)             driver = { id, name, phoneLast4 }
 *   - placeholder
 *   - disabled
 */

const driverCache = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000;
const MAX_VISIBLE = 50;

function toDriver(u) {
  const name = `${u?.name || ""} ${u?.lastName || ""}`.trim() || u?.email || "";
  const digits = String(u?.phone || "").replace(/\D/g, "");
  return {
    id: String(u?._id || u?.id || ""),
    name,
    phoneLast4: digits.length >= 4 ? digits.slice(-4) : "",
  };
}

function getActiveDrivers(lcid) {
  const now = Date.now();
  const entry = driverCache.get(lcid);
  if (entry && entry.expiresAt > now) return entry.promise;

  const promise = getActiveUsers(1, 10000, "", "delivery", "", "", "").then((res) => {
    if (res?.error) throw new Error("fetch drivers failed");
    const docs = res?.data?.docs || res?.data?.users || [];
    return docs
      .map(toDriver)
      .filter((d) => d.id && d.name)
      .sort((a, b) => a.name.localeCompare(b.name, "es"));
  });
  // A failed fetch must not stay cached for the whole TTL.
  promise.catch(() => driverCache.delete(lcid));
  driverCache.set(lcid, { promise, expiresAt: now + CACHE_TTL_MS });
  return promise;
}

export function invalidateDriverCache(lcid) {
  if (lcid) driverCache.delete(lcid);
  else driverCache.clear();
}

export default function DriverSelector({ value, onChange, placeholder, disabled = false }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const logisticCompanyId = user?.logisticCompanyId?._id || user?.logisticCompanyId || "";

  // null = not loaded yet; the fetch happens the first time the list opens.
  const [drivers, setDrivers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  const loadDrivers = useCallback(() => {
    if (!logisticCompanyId || loading) return;
    setLoading(true);
    setFetchError(false);
    getActiveDrivers(logisticCompanyId)
      .then((list) => setDrivers(list))
      .catch(() => {
        setDrivers(null);
        setFetchError(true);
      })
      .finally(() => setLoading(false));
  }, [logisticCompanyId, loading]);

  useEffect(() => {
    if (open && drivers === null && !fetchError) loadDrivers();
  }, [open, drivers, fetchError, loadDrivers]);

  const filtered = useMemo(() => {
    if (!drivers) return [];
    const q = query.trim().toLowerCase();
    if (!q) return drivers.slice(0, MAX_VISIBLE);
    return drivers.filter((d) => d.name.toLowerCase().includes(q)).slice(0, MAX_VISIBLE);
  }, [drivers, query]);

  // Click outside → close
  useEffect(() => {
    function onDocClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Keep the highlighted row visible while navigating with the arrows.
  useEffect(() => {
    if (!open || highlight < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${highlight}"]`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [highlight, open]);

  const select = (driver) => {
    onChange?.(driver);
    setQuery("");
    setOpen(false);
    setHighlight(-1);
  };

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlight >= 0 && filtered[highlight]) select(filtered[highlight]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlight(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || t("Buscar repartidor…")}
          aria-label={t("Buscar repartidor")}
          disabled={disabled}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls="driver-selector-listbox"
          role="combobox"
          className={cn(
            // Same `!` rationale as CommerceSelector: _modern-forms.scss styles
            // input[type="text"] with higher specificity than the utilities.
            "w-full h-8 !pl-8 !pr-7 !py-0 !text-xs rounded-md border border-border bg-bg text-fg placeholder:text-fg-subtle",
            "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50",
          )}
        />
        <Truck
          className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted pointer-events-none"
          aria-hidden="true"
        />
        <ChevronDown
          className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-fg-subtle pointer-events-none"
          aria-hidden="true"
        />
      </div>

      {open && !disabled && (
        <ul
          ref={listRef}
          id="driver-selector-listbox"
          role="listbox"
          aria-label={t("Repartidores activos")}
          className="absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-y-auto bg-bg border border-border rounded-md shadow-elevated py-1"
        >
          {loading && (
            <li className="px-3 py-2 text-xs text-fg-muted">{t("Cargando repartidores…")}</li>
          )}

          {!loading && fetchError && (
            <li className="px-3 py-2 text-xs text-status-error">
              <span>{t("No se pudieron cargar los repartidores.")}</span>{" "}
              <button
                type="button"
                // Clearing the error lets the open-list effect fetch again.
                onClick={() => setFetchError(false)}
                className="underline font-medium"
              >
                {t("Reintentar")}
              </button>
            </li>
          )}

          {!loading && !fetchError && drivers && filtered.length === 0 && (
            <li className="px-3 py-2 text-xs text-fg-muted">
              {query.trim() ? t("Sin resultados") : t("No hay repartidores activos")}
            </li>
          )}

          {!loading &&
            filtered.map((d, i) => {
              const isSelected = value?.id === d.id;
              const isHighlighted = highlight === i;
              return (
                <li
                  key={d.id}
                  role="option"
                  aria-selected={isSelected}
                  data-idx={i}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => select(d)}
                  className={cn(
                    "flex items-center gap-2 px-3 h-8 cursor-pointer text-xs",
                    isHighlighted
                      ? "bg-brand-50 text-brand-900"
                      : isSelected
                      ? "bg-bg-muted text-fg font-medium"
                      : "text-fg hover:bg-bg-muted/60",
                  )}
                >
                  <span className="flex-1 truncate" title={d.name}>
                    {d.name}
                  </span>
                  {d.phoneLast4 && (
                    <span className="text-[11px] text-fg-muted font-mono shrink-0">
                      ···{d.phoneLast4}
                    </span>
                  )}
                  {isSelected && (
                    <span className="text-[11px] uppercase tracking-wider text-brand-600 font-semibold shrink-0">
                      {t("Actual")}
                    </span>
                  )}
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
}
